import { css } from '@emotion/react';
import { t } from 'i18n';
import { Fragment, useMemo } from 'react';
import type { BoundFlight, Segment } from 'shared';
import { isDefined } from 'shared';

import { buildOperatedByString, getDurationInSec, getDurationInSecFromString } from '../../utils';
import { theme } from '../theme';

const styles = {
  durationBarAirportCodeLeft: css`
    margin-right: 4px;
    color: ${theme.colors.gray40};
    font-weight: 900;
    font-size: ${theme.fontSize.sm};
  `,
  durationBarAirportCodeRight: css`
    margin-left: 4px;
    color: ${theme.colors.gray40};
    font-weight: 900;
    font-size: ${theme.fontSize.sm};
  `,
  durationBarBase: css`
    width: 100%;
    display: flex;
    align-items: center;
  `,
  operatedBy: css`
    margin-top: ${theme.spacing.xs};
    font-size: ${theme.fontSize.xs};
    color: ${theme.colors.gray40};
  `,
  durationBarDot: css`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${theme.colors.blue40};
  `,
  durationBarWrapper: (flightDurationBarWidth: number) => css`
    width: ${flightDurationBarWidth}%;
    position: relative;
    display: flex;
    align-items: center;
    flex-basis: calc(var(--width) - 80px);
    border: ${theme.borders.widths.sm} solid ${theme.colors.blue30};
  `,
  durationBarVertical: (leftIndex: number) => css`
    left: ${leftIndex}%;
    z-index: 1;
    position: absolute;
    width: 1px;
    height: 6px;
    background-color: ${theme.colors.blue50};
  `,
  layoverDurationBar: (startIndex: number, endIndex: number) => css`
    left: ${startIndex}%;
    width: ${endIndex - startIndex}%;
    position: absolute;
    flex-basis: calc(var(--width) - 80px);
    border: ${theme.borders.widths.sm} solid ${theme.colors.gray90};
  `,
  durationBarHaltCode: css`
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: -20px;
    font-size: ${theme.fontSize.xs};
    color: ${theme.colors.gray40};
    font-weight: ${theme.fontWeight.bold};
  `,
};

type FlightDurationBarProps = {
  flight: BoundFlight | BoundFlight;
  longestFlightDuration: number;
  className?: string;
};

export const FlightDurationBar = ({ flight, longestFlightDuration, className }: FlightDurationBarProps) => {
  const { flightDurationBarWidth, segmentLayoverDuration, hopperStops } = durationBarValues(
    flight,
    longestFlightDuration
  );

  const operatedBy = useMemo(() => buildOperatedByString(flight), []);
  const originAirportCode = flight.origin.code;
  const destinationAirportCode = flight.destination.code;

  return (
    <div className={className}>
      <div css={styles.durationBarBase}>
        <span css={styles.durationBarAirportCodeLeft}>{originAirportCode}</span>
        <span css={styles.durationBarDot} />
        <div css={styles.durationBarWrapper(flightDurationBarWidth)}>
          {segmentLayoverDuration?.map((segment, index) => {
            return (
              <Fragment key={index}>
                <span css={styles.durationBarVertical(segment.layoverStartIndex)}></span>
                <span css={styles.layoverDurationBar(segment.layoverStartIndex, segment.layoverEndIndex)}>
                  <span css={styles.durationBarHaltCode}>{segment.layoverDestination}</span>
                </span>
                <span css={styles.durationBarVertical(segment.layoverEndIndex)}></span>
              </Fragment>
            );
          })}
          {hopperStops.map(hopperStop => {
            return <span key={hopperStop} css={styles.durationBarVertical(hopperStop)}></span>;
          })}
        </div>
        <span css={styles.durationBarDot} />
        <span css={styles.durationBarAirportCodeRight}>{destinationAirportCode}</span>
      </div>
      <div css={styles.operatedBy}>{t('operatedBy', { airline: operatedBy })}</div>
    </div>
  );
};

const durationBarValues = (flight: BoundFlight | BoundFlight, longestFlightDuration: number) => {
  const flightDurationBarWidth = buildDurationBarWidth(flight, longestFlightDuration);
  const segmentLayoverDuration = buildLayoverBarIndexAndDestination(flight);
  const hopperStops = buildHopperStops(flight);

  return { flightDurationBarWidth, segmentLayoverDuration, hopperStops };
};

const buildLayoverBarIndexAndDestination = (currentFlight: BoundFlight | BoundFlight) => {
  const flightDepartureTime = currentFlight.startDateTimeInGmt;
  const flightArrivalTime = currentFlight.endDateTimeInGmt;
  const fullFlightDurationInSec = getDurationInSec(flightArrivalTime, flightDepartureTime);

  return currentFlight.segments
    .map((segment: Segment, index: number) => {
      const nextSegmentDepartureTime = currentFlight.segments[index + 1]?.departureDateTimeInGmt;

      const layoverStartIndex =
        (getDurationInSec(segment.arrivalDateTimeInGmt, flightDepartureTime) / fullFlightDurationInSec) * 100;

      const layoverEndIndex =
        (getDurationInSec(nextSegmentDepartureTime, flightDepartureTime) / fullFlightDurationInSec) * 100;

      const layoverDestination = segment.arrivalAirport.code;

      return { layoverStartIndex, layoverEndIndex, layoverDestination };
    })
    .slice(0, -1);
};

const buildHopperStops = (currentFlight: BoundFlight | BoundFlight) => {
  const flightDepartureTime = currentFlight.startDateTimeInGmt;
  const flightArrivalTime = currentFlight.endDateTimeInGmt;
  const fullFlightDurationInSec = getDurationInSec(flightArrivalTime, flightDepartureTime);

  return currentFlight.segments
    .map((segment: Segment) => {
      if (!segment.via || segment.via.length === 0) {
        return undefined;
      }

      const segmentStartIndex =
        getDurationInSec(segment.departureDateTimeInGmt, flightDepartureTime) / fullFlightDurationInSec;
      const segmentDurationProcentage = getDurationInSecFromString(segment.duration) / fullFlightDurationInSec;

      const segmentHopperStartIndex = segmentDurationProcentage / 2;

      return (segmentStartIndex + segmentHopperStartIndex) * 100;
    })
    .filter(isDefined);
};

const buildDurationBarWidth = (currentFlight: BoundFlight | BoundFlight, longestFlight: number) => {
  const currentFlightDuration = getDurationInSecFromString(currentFlight.connectionDuration);
  return Math.floor((currentFlightDuration / longestFlight) * 75);
};
